import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/admin/AdminLayout";
import ImageUploader from "@/components/admin/ImageUploader";
import GalleryUploader from "@/components/admin/GalleryUploader";
import MultiAthletePicker from "@/components/admin/MultiAthletePicker";
import { publicUrl } from "@/lib/storage";
import { toast } from "sonner";

type Product = {
  id: string;
  brand_name: string;
  category: string | null;
  product_name: string;
  price: string | null;
  image_path: string | null;
  image_paths: string[];
  product_url: string | null;
  quote: string | null;
  is_house_brand: boolean;
  published: boolean;
  sort_order: number;
};

type AthleteRef = { id: string; name: string };

type Editing = (Partial<Product> & { id?: string; athleteIds: string[] });

const empty = (): Editing => ({
  brand_name: "",
  category: "",
  product_name: "",
  price: "",
  image_path: null,
  image_paths: [],
  product_url: "",
  quote: "",
  is_house_brand: false,
  published: false,
  sort_order: 0,
  athleteIds: [],
});

const AdminProducts = () => {
  const [list, setList] = useState<Product[]>([]);
  const [athletes, setAthletes] = useState<AthleteRef[]>([]);
  const [links, setLinks] = useState<Record<string, string[]>>({});
  const [editing, setEditing] = useState<Editing | null>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const [p, a, pa] = await Promise.all([
      supabase.from("brand_products").select("*").order("sort_order").order("created_at", { ascending: false }),
      supabase.from("athletes").select("id,name").order("name"),
      supabase.from("product_athletes").select("product_id, athlete_id"),
    ]);
    if (p.error) toast.error(p.error.message);
    setList((p.data as any) ?? []);
    setAthletes((a.data as any) ?? []);
    const map: Record<string, string[]> = {};
    ((pa.data as any) ?? []).forEach((row: any) => {
      (map[row.product_id] ||= []).push(row.athlete_id);
    });
    setLinks(map);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startEdit = (p: Product) => {
    setEditing({ ...p, athleteIds: links[p.id] ?? [] });
  };

  const save = async () => {
    if (!editing) return;
    if (!editing.brand_name || !editing.product_name) {
      toast.error("Brand and product name required"); return;
    }
    if (editing.athleteIds.length === 0) {
      toast.error("Select at least one athlete"); return;
    }
    const payload: any = {
      brand_name: editing.brand_name,
      category: editing.category,
      product_name: editing.product_name,
      price: editing.price,
      image_path: editing.image_path,
      image_paths: editing.image_paths ?? [],
      product_url: editing.product_url || null,
      quote: editing.quote || null,
      is_house_brand: !!editing.is_house_brand,
      published: editing.published,
      sort_order: editing.sort_order ?? 0,
      // Maintain legacy column with first selected athlete for backward compat.
      athlete_id: editing.athleteIds[0],
    };
    let productId = editing.id;
    if (productId) {
      const { error } = await supabase.from("brand_products").update(payload).eq("id", productId);
      if (error) return toast.error(error.message);
    } else {
      const { data, error } = await supabase.from("brand_products").insert(payload).select("id").single();
      if (error) return toast.error(error.message);
      productId = data.id;
    }
    // Sync join table: delete existing then insert current selection.
    await supabase.from("product_athletes").delete().eq("product_id", productId!);
    if (editing.athleteIds.length > 0) {
      const rows = editing.athleteIds.map((aid) => ({ product_id: productId!, athlete_id: aid }));
      const { error: linkErr } = await supabase.from("product_athletes").insert(rows);
      if (linkErr) return toast.error(linkErr.message);
    }
    toast.success("Saved");
    setEditing(null);
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this product?")) return;
    const { error } = await supabase.from("brand_products").delete().eq("id", id);
    if (error) return toast.error(error.message);
    load();
  };

  const athleteNames = (id: string) =>
    (links[id] ?? []).map((aid) => athletes.find((a) => a.id === aid)?.name).filter(Boolean).join(", ") || "—";

  return (
    <AdminLayout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="font-display text-3xl">Brand products</h1>
          <p className="text-sm text-muted-foreground mt-1">{list.length} total</p>
        </div>
        <button
          onClick={() => setEditing(empty())}
          disabled={athletes.length === 0}
          className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold disabled:opacity-50"
        >
          + New product
        </button>
      </div>

      {loading ? <p className="text-muted-foreground">Loading…</p> : (
        <div className="grid gap-3">
          {list.map((p) => (
            <div key={p.id} className="bg-card rounded-2xl p-4 flex items-center gap-4 shadow-card">
              <div className="w-16 h-16 rounded-xl bg-muted overflow-hidden shrink-0">
                {p.image_path && <img src={publicUrl(p.image_path)!} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold truncate">{p.brand_name} — {p.product_name}</p>
                  {p.is_house_brand && (
                    <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-accent text-accent-foreground">Official</span>
                  )}
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${p.published ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                    {p.published ? "Live" : "Draft"}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{p.category} · {p.price} · via {athleteNames(p.id)}</p>
              </div>
              <button onClick={() => startEdit(p)} className="px-4 py-2 rounded-full bg-muted text-sm font-semibold">Edit</button>
              <button onClick={() => remove(p.id)} className="px-4 py-2 text-sm font-semibold text-destructive">Delete</button>
            </div>
          ))}
          {list.length === 0 && <p className="text-muted-foreground">No products yet.</p>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-card rounded-[1.5rem] shadow-bold max-w-xl w-full p-6 my-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-2xl">{editing.id ? "Edit" : "New"} product</h2>
              <button onClick={() => setEditing(null)} className="text-muted-foreground hover:text-foreground">✕</button>
            </div>
            <div className="space-y-4">
              <Field label="Cover image">
                <ImageUploader value={editing.image_path ?? null} onChange={(p) => setEditing({ ...editing, image_path: p })} prefix="products" />
              </Field>
              <Field label="Athletes *">
                <MultiAthletePicker
                  athletes={athletes}
                  selected={editing.athleteIds}
                  onChange={(ids) => setEditing({ ...editing, athleteIds: ids })}
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Brand *"><input value={editing.brand_name ?? ""} onChange={(e) => setEditing({ ...editing, brand_name: e.target.value })} className={inp} maxLength={50} /></Field>
                <Field label="Category"><input value={editing.category ?? ""} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inp} maxLength={50} /></Field>
                <Field label="Product *"><input value={editing.product_name ?? ""} onChange={(e) => setEditing({ ...editing, product_name: e.target.value })} className={inp} maxLength={100} /></Field>
                <Field label="Price"><input value={editing.price ?? ""} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className={inp} maxLength={20} placeholder="$249" /></Field>
              </div>
              <Field label="External purchase URL">
                <input
                  type="url"
                  value={editing.product_url ?? ""}
                  onChange={(e) => setEditing({ ...editing, product_url: e.target.value })}
                  className={inp}
                  placeholder="https://brand.com/product"
                />
              </Field>
              <Field label="Quote / blurb">
                <textarea
                  value={editing.quote ?? ""}
                  onChange={(e) => setEditing({ ...editing, quote: e.target.value })}
                  className={`${inp} min-h-[80px]`}
                  maxLength={500}
                  placeholder="“These boots changed my game.” — Maya"
                />
              </Field>
              <Field label="Additional images">
                <GalleryUploader
                  value={editing.image_paths ?? []}
                  onChange={(paths) => setEditing({ ...editing, image_paths: paths })}
                  prefix="products"
                />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Sort order"><input type="number" value={editing.sort_order ?? 0} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) || 0 })} className={inp} /></Field>
                <label className="flex items-center gap-3 px-4 py-3 rounded-xl bg-muted cursor-pointer">
                  <input type="checkbox" checked={!!editing.is_house_brand} onChange={(e) => setEditing({ ...editing, is_house_brand: e.target.checked })} className="w-5 h-5 accent-primary" />
                  <span className="font-semibold text-sm">Our branded good</span>
                </label>
                <label className="flex items-center gap-3 px-4 py-3 rounded-xl bg-muted cursor-pointer col-span-2">
                  <input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="w-5 h-5 accent-primary" />
                  <span className="font-semibold text-sm">Published</span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="px-5 py-2.5 rounded-full bg-muted font-semibold">Cancel</button>
              <button onClick={save} className="px-5 py-2.5 rounded-full bg-primary text-primary-foreground font-semibold">Save</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

const inp = "w-full px-3 py-2 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary text-sm";
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

export default AdminProducts;
